import { SubAPI } from "./SubAPI";
import {Paginated} from "./Users";
import {
    HardwareGroup,
    HardwareGroupUpsert,
    HardwareGroupUpsertResponse, MergeCandidate, MergeGroup,
    BulkEditItem, PrepDetail, PrepFromGroup,
} from "./QuoteHardware";
import {Opening, OpeningLogiCtrlStatus} from "./Openings";
import {DiscontinuedProduct, ProductReviewStatus} from "./Products";

export class Hardware extends SubAPI {
    bulkUpdateOpenings(input: {
        hardwareGroup: number;
        project: number;

        openings: Opening & {
            hwGroupAssign: string;
        }[];
    }) {
        return this.fetcher.post("/api/hardware/bulk-update-openings", input);
    }

    bulkEditProduct(input: {
        project: number;
        items: BulkEditItem[];
    }) {
        return this.fetcher.post("/api/hardware/bulk-edit-product", input)
    }

    reSequence(input: {
        project: number;
    }) {
        return this.fetcher.post("/api/hardware/re-sequence", input);
    }

    updateHardwareGroupLink(input: {
        opening: number;
        group: number;
        link: boolean
    }) {
        return this.fetcher.post("/api/hardware/edit-opening-link", input)
    }

    overwriteFromQuoteMode(input: {
        project: number;
        alternative: number;
        autoFix?: boolean;
    }) {
        return this.fetcher.post("/api/hardware/overwrite-from-quote-mode", input)
    }

    listDistributorDetails(input: {
        project: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<DistributorDetail>>("/api/hardware/distributor-detail", input)
    }

    updateDistributors(input: {
        project: number;
        changes: {
            product: number;
            overrideDistributor: number | null;
        }[]
    }) {
        return this.fetcher.post("/api/hardware/distributor-detail", input)
    }

    listWithContents(input: {
        project: number;
        previewChangeset?: number;
        groups?: number[];
    }) {
        return this.fetcher.get<HardwareGroupWithContent[]>("/api/hardware/with-content", {
            project: input.project,
            previewChangeset: input.previewChangeset,
            groups: input.groups ? input.groups.join(",") : undefined,
        })
    }

    singleWithContent(input: {
        opening: number;
    }) {
        return this.fetcher.get<HardwareGroupWithContent|null>("/api/hardware/single-with-content", input)
    }

    list(input: {
        project: number;
        search?: string;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<HardwareGroup>>("/api/hardware", input)
    }

    listFinishes(input: {
        manufacturer: number;
        offset: number;
    }) {
        return this.fetcher.get<Paginated<Finish>>("/api/hardware/manufacturer-finishes", input)
    }

    upsert(input: HardwareGroupUpsert) {
        return this.fetcher.post<HardwareGroupUpsertResponse>("/api/hardware", input)
    }

    upsertTask(input: HardwareGroupUpsert) {
        return this.fetcher.post<{task: string}>("/api/hardware/task", input)
    }

    upsertItemSecondary(input: HardwareItem) {
        return this.fetcher.post("/api/hardware/item-secondary", input);
    }

    mergeCandidates(input: {
        project: number;
        splitFirst?: boolean;
    }) {
        return this.fetcher.get<MergeCandidate[]>("/api/hardware/merge-candidates", input)
    }

    merge(input: {
        project: number;
        groups: MergeGroup[];
        resequence: boolean;
        autoMerge?: boolean;
    }) {
        return this.fetcher.post("/api/hardware/merge", input)
    }

    makePrepImage(input: {
        key: string;
        file?: Blob
    }) {
        return this.fetcher.postFormData<{
            status: "key-missing"
        } | {
            status: "exists" | "uploaded"
            url: string;
        }>("/api/hardware/make-prep-image", input)
    }
}

export type DistributorDetail = {
    id: number;
    product: number;
    name: string;
    productCode: string;
    manufacturer: number;
    manufacturerName: string;

    distributor?: number;
    distributorName?: string;

    distributorOverride?: number;
    distributorOverrideName?: string;
}

export type Finish = {id: number, finish: string; archived: boolean;}

export type HardwareGroupWithContent = {
    id: number;
    name: string;
    project: number;
    notes: string;
    prepConfigJson?: PrepDetail[]
    prepConfigIsCustom?: boolean;
    flipDoubleActiveHanding: boolean;
    quoteAlternative?: number;

    openings: HwOpening[];
    hardware: HardwareItem[];

    backup?: Omit<HardwareGroupWithContent, "backup"|"hardware"|"openings">;
}

export type HwOpening = {
    seqNumber: number;
    phase: string;
    name: string,
    descriptiveName: string;
    doorName: string;
    handing: string;
    id: number,
    qty: number;
    sessionChanged: boolean
    nominalHeight: string;
    nominalWidth: string;
    inactiveDoorWidth: string;
    screenElevation: string;
    screenElevationFile?: number;
    openingType: string;
    frameProfile: string;
    degreesOfOpeningActive: number | null;
    degreesOfOpeningInactive: number | null;

    // set when the opening's hinge/prep layout is owned by the drw elevation tool.
    drwHardwareElevation?: number;
    drwHardwareElevationName?: string;
    prepFromGroup?: PrepFromGroup;

    logictrl: OpeningLogiCtrlStatus | null;
}

export type HardwareItem = {
    id: number;
    product: number;
    baseProduct?: number; // dynamic-code template when product is a dimension variant, else product
    baseProductCode?: string;
    manufacturer: number;
    manufacturerName: string;
    manufacturerShortName: string;
    name: string;
    productCode: string;
    finish: string;
    finishName: string;
    dimHeight: string;
    dimWidth: string;
    dimLength: string;
    dimensions: string;
    dimensionFormula?: number;
    activeQty: number;
    inactiveQty: number;
    commonQty: number;
    isCustom: boolean;
    productIsAutoQtyHinge?: boolean;
    shortcode?: number;
    shortcodeItem?: number;
    shortcodeName?: string;
    shortcodeDescription?: string;
    customerSupplied: boolean
    supplierCategory: number;
    supplierCategoryName: string;

    reviewStatus?: ProductReviewStatus | null;

    discontinued: boolean;
    discontinuedReplaceWith: DiscontinuedProduct[];

    hingePrepLocationConfig?: {
        active: number[];
        inActive: number[]
    } | null

    category: string;
    category2?: string | null;
    category3?: string | null;

    hwPrepSingleActiveDoor?: string;
    hwPrepSingleActiveDoorCustomTemplate?: number;
    hwPrepSingleFrame?: string;
    hwPrepSingleFrameCustomTemplate?: number;
    hwPrepPairActiveDoor?: string;
    hwPrepPairActiveDoorCustomTemplate?: number;
    hwPrepPairBothActiveDoor?: string;
    hwPrepPairBothActiveDoorCustomTemplate?: number;
    hwPrepPairInactiveDoor?: string;
    hwPrepPairInactiveDoorCustomTemplate?: number;
    hwPrepPairFrame?: string;
    hwPrepPairFrameCustomTemplate?: number;

    calcTotalQty: number; // applies the active/inactive/common rules to the doors in the group
    calcByDimension: DimensionedQty

    note: string;
    archived: boolean;

    backup?: Omit<HardwareItem, "backup">;

    hingeActiveQtys?: number[];
    hingeInactiveQtys?: number[];

    overrideHingeActiveQty: number | null;
    overrideHingeInActiveQty: number | null;

    warnings: string[] | null;
    electricStrikeConfig: {
        activeLeaf?: boolean;
        inactiveLeaf?: boolean;
    };
}

export const warnNeedElectricStrikeConfig = "Need to configure per leaf"
export const warnRemoveElectricStrikeConfig = "Remove per leaf configuration"
export const warnMissingDegreesPrefix = "Missing degrees of opening "

export type DimensionedQty = {
    id: number; //product
    dimension: string
    qty: number;
    activeQty: number;
    inactiveQty: number;
    commonQty: number;
    productCode: string;

    openingDetail: DimOpeningDetail[] | null
}[] | null | undefined

type DimOpeningDetail = {
    opening: number;
    qty: number;
    subType: string;
    error?: string | null;
}